import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, FileText, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreateWorkCardModal = ({ open, onClose, customers, onSuccess }) => {
  const [customerId, setCustomerId] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  
  // New customer fields
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    address: '',
    phone: '',
    email: ''
  });

  const handleCreateCustomer = async () => {
    if (!newCustomer.name) {
      toast.error('Kundens navn er påkrævet');
      return;
    }

    try {
      const response = await axios.post(`${API}/customers`, newCustomer);
      toast.success('Kunde oprettet!');
      setCustomerId(response.data.id);
      setShowNewCustomer(false);
      setNewCustomer({ name: '', address: '', phone: '', email: '' });
      onSuccess(); // Refresh customers list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke oprette kunde');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!customerId) {
      toast.error('Vælg en kunde');
      return;
    }
    
    if (!taskDescription.trim()) {
      toast.error('Indtast arbejdsopgave');
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/workcards`, {
        customer_id: customerId,
        task_description: taskDescription
      });
      toast.success('Arbejdskort oprettet!');
      setCustomerId('');
      setTaskDescription('');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke oprette arbejdskort');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setCustomerId('');
    setTaskDescription('');
    setShowNewCustomer(false);
    setNewCustomer({ name: '', address: '', phone: '', email: '' });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" data-testid="create-workcard-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Opret Arbejdskort
          </DialogTitle>
          <DialogDescription>
            Udfyld oplysningerne for at oprette et nyt arbejdskort
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Customer Selection */}
          <div className="space-y-2">
            <Label>Kunde</Label>
            {!showNewCustomer ? (
              <div className="flex gap-2">
                <Select value={customerId} onValueChange={setCustomerId}>
                  <SelectTrigger className="flex-1" data-testid="customer-select">
                    <SelectValue placeholder="Vælg kunde..." />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowNewCustomer(true)}
                  data-testid="new-customer-btn"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="space-y-3 p-4 border rounded-lg bg-muted/50">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Ny Kunde
                  </h4>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowNewCustomer(false)}
                  >
                    Annuller
                  </Button>
                </div>
                <div className="grid gap-3">
                  <Input
                    placeholder="Fulde navn *"
                    value={newCustomer.name}
                    onChange={(e) => setNewCustomer({...newCustomer, name: e.target.value})}
                    data-testid="new-customer-name"
                  />
                  <Input
                    placeholder="Adresse"
                    value={newCustomer.address}
                    onChange={(e) => setNewCustomer({...newCustomer, address: e.target.value})}
                    data-testid="new-customer-address"
                  />
                  <Input
                    placeholder="Telefon"
                    value={newCustomer.phone}
                    onChange={(e) => setNewCustomer({...newCustomer, phone: e.target.value})}
                    data-testid="new-customer-phone"
                  />
                  <Input
                    placeholder="Email"
                    type="email"
                    value={newCustomer.email}
                    onChange={(e) => setNewCustomer({...newCustomer, email: e.target.value})}
                    data-testid="new-customer-email"
                  />
                  <Button
                    type="button"
                    onClick={handleCreateCustomer}
                    className="w-full"
                    data-testid="save-customer-btn"
                  >
                    Gem Kunde
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Task Description */}
          <div className="space-y-2">
            <Label htmlFor="task">Arbejdsopgave</Label>
            <Textarea
              id="task"
              placeholder="Beskriv arbejdsopgaven..."
              value={taskDescription}
              onChange={(e) => setTaskDescription(e.target.value)}
              rows={4}
              data-testid="task-description-input"
            />
          </div>

          {/* Submit */}
          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
            >
              Annuller
            </Button>
            <Button
              type="submit"
              disabled={isLoading || showNewCustomer}
              className="flex-1"
              data-testid="submit-workcard-btn"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                  Opretter...
                </span>
              ) : (
                'Opret Arbejdskort'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateWorkCardModal;
